/*
 * Copyright (c) Sureify Labs 2020.
 * This source code is licensed under MIT License.
 * @author jayendrasharan <jayendra@sureify.com>
 * Created at: 03/05/20, 4:57 AM
 * Last Modified at: 03/05/20, 2:49 AM
 */

import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { ThemeProvider } from 'styled-components'
import { Button } from '../Button'
import Radio from './Radio'
import {
  RadioGroupWrapper,
  CBBottomButtonWrapper,
  CBFullButtonWrapper,
  CBFullDescription
} from './styles'
import { ErrorMessage, TooltipWrapper, HelperText } from '../../styles'
import { Tooltip } from '../Tooltip'
import { themes } from '../../themes'
import { useDocumentWidth } from '../../utils'

import '@fortawesome/fontawesome-free/css/fontawesome.min.css'
import '@fortawesome/fontawesome-free/css/solid.min.css'

const RadioGroup = (props) => {
  const {
    options,
    name,
    inputRef,
    orientation,
    shape,
    overlap,
    labelWidth,
    color,
    label,
    columns,
    onChange,
    checked,
    hasTooltip,
    tooltipTitle,
    tooltipBody,
    tooltipHTML,
    overlapWithMargin,
    hasBorderTop,
    showCustomInput,
    themeConfiguration,
    disabled,
    customPadding,
    onActionBtnClick,
    cardButtonTheme,
    id,
    displayCardHeader,
    hideCardButtonArrow,
    helperText
  } = props

  const { floatingLabel, labelPosition } = props

  const inlineTooltip = hasTooltip && !floatingLabel && labelPosition === 'top'

  const theme = themeConfiguration || themes[color]

  const docWidth = useDocumentWidth()

  const [selectedRadio, setSelectedRadio] = useState(
    options.find((option) => option.id === checked)
  )

  const [hasError, sethasError] = useState('')
  const [errorMsg, setErrorMsg] = useState('')

  const showHelperText = helperText && !hasError
  const HelperTextComponent = (
    <HelperText
      data-testid='helper-text'
      aria-live='polite'
    >
      {helperText}
    </HelperText>
  )

  const ErrorComponent = (
    <ErrorMessage data-testid='error-message'>{errorMsg}</ErrorMessage>
  )

  useEffect(() => {
    if (selectedRadio?.error_message) {
      setErrorMsg(selectedRadio.error_message)
      sethasError(true)
    } else {
      setErrorMsg('')
      sethasError(false)
    }
  }, [selectedRadio])

  const onClickOption = (e, o) => {
    setSelectedRadio(o)
    if (!disabled) {
      onChange(e, o)
    }
  }
  return (
    <ThemeProvider
      theme={{
        showCustomInput,
        hasBorderTop,
        color,
        shape,
        overlap,
        orientation,
        columns,
        overlapWithMargin,
        disabled,
        labelWidth,
        customPadding,
        hasError,
        ...theme
      }}
    >
      {label && <div dangerouslySetInnerHTML={{ __html: label }} />}
      <RadioGroupWrapper
        className='radio-group-wrapper'
        role='radiogroup'
        aria-labelledby={`${id}-questionText`}
      >
        {options.map((o) => (
          <Radio
            id={`${id}_${o.id}`}
            key={o.id}
            icon={o.icon}
            inputRef={inputRef}
            labelText={o.label}
            content={o.content}
            name={name}
            shape={shape}
            hasBorderTop={hasBorderTop}
            onChange={(e) => {
              onClickOption(e, o)
            }}
            checked={o.id === checked}
            disabled={disabled}
            onActionBtnClick={() => onActionBtnClick(o)}
            description={o.description}
            cardButton={o.button}
            cardButtonTheme={cardButtonTheme}
            productDetails={o.product_details}
            docWidth={docWidth}
            displayCardHeader={displayCardHeader}
            cardHeaderValue={o.card_header}
            hideCardButtonArrow={hideCardButtonArrow}
          />
        ))}
        {hasTooltip && (
          <TooltipWrapper
            theme={{ shape }}
            name='tool-tip-wrapper'
            position={inlineTooltip ? 'inline' : 'none'}
          >
            <Tooltip
              name='tool-tip'
              tooltipTitle={tooltipTitle}
              tooltipBody={tooltipBody}
              tooltipHTML={tooltipHTML}
            />
          </TooltipWrapper>
        )}
        {(shape === 'card_button' && checked) || overlapWithMargin ? null : (
          showHelperText ? HelperTextComponent : ErrorComponent
        )}
      </RadioGroupWrapper>
      {shape === 'card_button' &&
        !hideCardButtonArrow &&
        (docWidth === window.innerWidth ? docWidth : window.innerWidth) <
          768 && (
          <CBBottomButtonWrapper>
            <CBFullButtonWrapper className='cb_full_button'>
              <Button
                label={
                  options.length && options[0] && options[0].button
                    ? options[0].button.label
                    : ''
                }
                alignment='center'
                themeConfiguration={cardButtonTheme}
                handleButtonClick={() => onActionBtnClick(selectedRadio)}
              />
              <CBFullDescription>
                {options.length && options[0] ? options[0].description : ''}
              </CBFullDescription>
            </CBFullButtonWrapper>
          </CBBottomButtonWrapper>
        )}
    </ThemeProvider>
  )
}

RadioGroup.defaultProps = {
  shape: 'classic',
  orientation: 'rows',
  overlap: false,
  color: 'blue',
  columns: 0,
  overlapWithMargin: false,
  hasBorderTop: false,
  showCustomInput: false,
  disabled: false,
  onActionBtnClick: () => {}
}

RadioGroup.propTypes = {
  /**
   * Label for the radio group.
   */
  label: PropTypes.string,
  /**
   * Description of all available options
   */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  /**
   * Ref for Radio Group.
   */
  inputRef: PropTypes.object,
  /**
   * Shape of the radio buttons
   */
  shape: PropTypes.oneOf([
    'classic',
    'oval',
    'rectangle',
    'card_columns',
    'card',
    'icon_card'
  ]),
  /**
   * A name for the group of buttons.
   */
  name: PropTypes.string.isRequired,
  /**
   * Orientation of the buttons inside the group. In case of row, the buttons
   * be wrapped to next row if the buttons do not fit on the screen width.
   */
  orientation: PropTypes.oneOf(['rows', 'columns']),
  /**
   * Determines the spacing between the radio buttons, only valid for `orientation: row`.
   */
  overlap: PropTypes.bool,
  /**
   * Determines the color theme of the buttons.
   */
  color: PropTypes.string,
  /**
   * Number of columns in a row = if the orientation is `rows` then we can
   * divide the items in 1, 2..5 columns.
   * For example: Orientation: `rows` and Columns: `1` is equivalent of Orientation: `columns`.
   */
  columns: PropTypes.number,
  /**
   * Callback function to be called when selected
   */
  onChange: PropTypes.func.isRequired,
  /**
   * Default checked radio, id
   */
  checked: PropTypes.string,
  /**
   * margin inside the RadioGroupWrapper
   */
  overlapWithMargin: PropTypes.bool,
  /**
   * this gives a top border on rectangle
   */
  /**
   * Adds border at top
   */
  hasBorderTop: PropTypes.bool,
  /**
   * Shows custom input
   */
  showCustomInput: PropTypes.bool,
  /**
   * Theme configuration object
   */
  themeConfiguration: PropTypes.object,
  /**
   * disbled state of Radio button
   */
  disabled: PropTypes.bool,
  /**
   * custom Padding values
   */
  customPadding: PropTypes.string,
  /**
   * to handle button click in card_button shape's radio
   */
  onActionBtnClick: PropTypes.func,
  /**
   * Theme to be applied to button for card_button shape
   */
  cardButtonTheme: PropTypes.object
}

export default RadioGroup
